import React, { useEffect, useState, ErrorInfo, Suspense } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr, enUS } from 'date-fns/locale';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Alert, Box, Typography } from '@mui/material';
import { AuthProvider } from '../../contexts/AuthContext';
import { NotificationProvider } from '../../contexts/NotificationContext';
import { BrowserRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { theme } from '../../theme/theme';
import { PWAInstallPrompt } from '../common/PWAInstallPrompt';
import { PWAUpdateNotification } from '../common/PWAUpdateNotification';

interface AppProvidersProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="h6">Something went wrong</Typography>
            <Typography variant="body2">{this.state.error?.message}</Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Please try refreshing the page. If the problem persists, contact support.
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

export const AppProviders: React.FC<AppProvidersProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const [locale, setLocale] = useState(i18n.language === 'fr' ? fr : enUS);

  useEffect(() => {
    setLocale(i18n.language === 'fr' ? fr : enUS);
  }, [i18n.language]);

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
          <CssBaseline />
          <BrowserRouter>
            <AuthProvider>
              <NotificationProvider>
                <Suspense fallback={null}>
                  <PWAInstallPrompt />
                  <PWAUpdateNotification />
                  {children}
                </Suspense>
              </NotificationProvider>
            </AuthProvider>
          </BrowserRouter>
        </LocalizationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};
